'use client';

import { useTranslation } from 'react-i18next';

import { LinkStyle } from '@sola/ark-core/atoms/link-style/link-style.js';
import { ActionCardFooter } from '@sola/ark-core/molecules/action-card/action-card.js';
import {
  GraphQLCommitment,
  GraphQLSupporterPaymentMethod,
} from '@sola/schema/dotcom/supporters/schema.generated.js';
import { useSupporterQuery } from '@sola/supporter-sdk/hooks.js';
import {
  commitmentsQuery,
  paymentMethodsQuery,
} from '@sola/supporter-sdk/queries.js';

import { PaymentMethodRemoveDialog } from '../../organisms/payment-method-remove-dialog/payment-method-remove-dialog';
import { PaymentMethodUpdateSheet } from '../../organisms/payment-method-update-sheet/payment-method-update-sheet';

export const useComponentData = (
  currentPaymentMethod: GraphQLSupporterPaymentMethod,
) => {
  // @ts-expect-error TS doesn't know yet what useSupporterQuery will actually return
  const {
    data: paymentMethodData,
  }: { data: [GraphQLSupporterPaymentMethod[]] } = useSupporterQuery([
    paymentMethodsQuery,
    commitmentsQuery,
  ]);

  const [paymentMethods] = paymentMethodData;

  return {
    alternatePaymentMethods: paymentMethods
      ? paymentMethods.filter(
          (pm) => pm.id !== currentPaymentMethod.id && pm.status !== 'Expired',
        )
      : [],
  };
};

export type PaymentMethodCardFooterProps = {
  paymentMethod: GraphQLSupporterPaymentMethod;
  recurringCommitments?: GraphQLCommitment[];
  onRemoveSuccess?: () => void;
  onRemoveError?: () => void;
};

export const PaymentMethodCardFooter: React.FC<
  PaymentMethodCardFooterProps
> = ({ paymentMethod, recurringCommitments = [] }) => {
  const { t } = useTranslation('my');

  const { alternatePaymentMethods } = useComponentData(paymentMethod);

  const isShowingRemoveBtn =
    !recurringCommitments.length || !!alternatePaymentMethods?.length;

  return (
    <ActionCardFooter justify="end">
      <div className="flex justify-end items-center gap-comfy-sm md:gap-comfy-md">
        <div className="flex gap-comfy-md ml-auto">
          {isShowingRemoveBtn && (
            <PaymentMethodRemoveDialog
              openTrigger={
                <LinkStyle noUnderline noPadding size="lg" purpose="negative">
                  <button type="button">{t('payments.removeMethod')}</button>
                </LinkStyle>
              }
              selectedPaymentMethod={paymentMethod}
              recurringCommitments={recurringCommitments}
              alternatePaymentMethods={alternatePaymentMethods}
            />
          )}

          <PaymentMethodUpdateSheet
            openTrigger={
              <LinkStyle noUnderline noPadding size="lg">
                <button type="button">{t('payments.editMethod')}</button>
              </LinkStyle>
            }
            paymentMethod={paymentMethod}
          />
        </div>
      </div>
    </ActionCardFooter>
  );
};
